import { Button, Icon } from '@iwoca/orion';
import styles from './TrueLayerLink.module.css';
import { fetchPostTruelayerAuthorizationUrl } from '@iwoca/lapi-client/edge';
import { useStateKey } from '../../../../../../../iwQueryClient/useStateKey';
import { useMutation } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from './TrueLayerLink.messages';
import TrueLayerLinkedAccounts from '../../../../../../../myAccount/modals/linkOpenBankingModal/trueLayer/TrueLayerLinkedAccounts';
import { useEffect, useState } from 'react';
import { getQueryParameter, getWindowLocation } from '@iwRouter/LocationHelper';
import {
    TRUELAYER_CANCEL,
    TRUELAYER_FAILURE,
    TRUELAYER_SUCCESS
} from '../../../../../../../myAccount/modals/linkOpenBankingModal/trueLayer/truelayer.constants';
import { TruelayerAlertBox } from '../../../../../../../myAccount/modals/linkOpenBankingModal/trueLayer/TruelayerAlertbox';
import { tracking } from '@iwoca/frontend-tracking-library';

const TrueLayerLink = ({
    toggleManualUpload
}: {
    toggleManualUpload: () => void;
}) => {
    const stateKey = useStateKey();
    const intl = useIntl();

    const [hasAuthorisationUrlErrored, setHasAuthorisationUrlError] =
        useState(false);

    const { isLoading, mutate: fetchTruelayerUrl } = useMutation({
        mutationFn: () =>
            fetchPostTruelayerAuthorizationUrl({ stateKey: stateKey! }),
        onSuccess: (data) => {
            if (data?.data?.authorization_url) {
                window.location.href = data.data.authorization_url;
            }
        },
        onError: () => {
            setHasAuthorisationUrlError(true);
        }
    });

    useEffect(() => {
        const statusCode = getQueryParameter(window.location, 'status_code');

        if (statusCode === '200') {
            tracking.ampli.newDocUploadsSuccessfullyLinkedBankAccountViaTruelayer();
        }
    }, []);

    const alertStatus = (() => {
        if (hasAuthorisationUrlErrored) {
            return TRUELAYER_FAILURE;
        }

        const location = getWindowLocation();
        const statusCode = getQueryParameter(location, 'status_code');
        const errorDetail = getQueryParameter(location, 'error_detail');
        if (statusCode) {
            if (statusCode === '200') {
                return TRUELAYER_SUCCESS;
            }

            if (
                statusCode === '500' &&
                errorDetail &&
                errorDetail === 'access_denied'
            ) {
                return TRUELAYER_CANCEL;
            }

            if (statusCode === '500' && errorDetail !== 'access_denied') {
                return TRUELAYER_FAILURE;
            }

            return TRUELAYER_FAILURE;
        }

        return null;
    })();

    const handleClick = () => {
        fetchTruelayerUrl();
    };

    return (
        <div className={styles.container}>
            <h5 className={styles.title}>Link my bank account</h5>
            <div className={styles.mainSection}>
                <ul className={styles.infoSection}>
                    <li>
                        <FormattedMessage
                            {...messages.info}
                            values={{
                                FCALink: (
                                    <a
                                        href={intl.formatMessage(
                                            messages.FCALink
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FormattedMessage
                                            {...messages.FCALinkText}
                                        />
                                    </a>
                                ),
                                link: (
                                    <a
                                        href={intl.formatMessage(
                                            messages.infoLink
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FormattedMessage
                                            {...messages.infoLinkText}
                                        />
                                    </a>
                                )
                            }}
                        />
                    </li>
                    <li>
                        <FormattedMessage {...messages.infoTwo} />
                    </li>
                </ul>

                <Button
                    className={styles.linkBankCTA}
                    onClick={handleClick}
                    variant="secondary"
                    loading={isLoading}
                    type="button"
                >
                    <FormattedMessage {...messages.linkCTA} />
                    <Icon icon="link" className={styles.linkIcon} />
                </Button>
            </div>

            <div className={styles.linkedAccountsSection}>
                <TrueLayerLinkedAccounts />
            </div>

            {alertStatus && (
                <div className={styles.alertsSection}>
                    <TruelayerAlertBox
                        handleFailureButtonClick={toggleManualUpload}
                        handleCancelButtonClick={toggleManualUpload}
                        status={alertStatus}
                    />
                </div>
            )}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default TrueLayerLink;
